import { ReactNode, createContext, useContext, useMemo } from 'react';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import type { FaqItemData } from './faqProvider';
import { FaqProvider, useFaq } from './faqProvider';
import type { HelpDocumentData } from './helpCenterProvider';
import { HelpDocumentProvider, useHelpDocuments } from './helpCenterProvider';
import type { VideoItemData } from './videosProvider';
import { VideosProvider, useVideos } from './videosProvider';
import type { ResourceEntry } from '../utils/resourceIndex';
import { buildResourceIndex } from '../utils/resourceIndex';

/**
 * Mounts the three content providers together and derives the flat index the
 * redesign's cross-cutting surfaces need: the ⌘K palette searches everything,
 * and the overview page counts and links across all three types.
 *
 * The individual providers stay exported and usable on their own — a page that
 * only needs videos should keep mounting `VideosProvider` alone.
 */
interface ResourcesLibraryContextValue {
    faqItems: FaqItemData[];
    helpDocuments: HelpDocumentData[];
    videos: VideoItemData[];
    /** All three types, flattened and searchable. */
    entries: ResourceEntry[];
    /** True while any of the three lists is still loading. */
    isLoading: boolean;
    /** First error across the three lists, if any. */
    error: string | null;
    refresh: (force?: boolean) => Promise<void>;
}

const ResourcesLibraryContext = createContext<ResourcesLibraryContextValue | undefined>(undefined);

function ResourcesLibraryValue({ children }: { children: ReactNode }) {
    const { basePath } = useResourcesConfig();
    const faq = useFaq();
    const help = useHelpDocuments();
    const videos = useVideos();

    const entries = useMemo(
        () =>
            buildResourceIndex(
                {
                    faqItems: faq.faqItems,
                    helpDocuments: help.helpDocuments,
                    videos: videos.videos
                },
                basePath
            ),
        [faq.faqItems, help.helpDocuments, videos.videos, basePath]
    );

    const value = useMemo<ResourcesLibraryContextValue>(
        () => ({
            faqItems: faq.faqItems,
            helpDocuments: help.helpDocuments,
            videos: videos.videos,
            entries,
            isLoading: faq.isLoading || help.isLoading || videos.isLoading,
            error: faq.error ?? help.error ?? videos.error,
            refresh: async (force?: boolean) => {
                await Promise.all([faq.refresh(force), help.refresh(force), videos.refresh(force)]);
            }
        }),
        [faq, help, videos, entries]
    );

    return (
        <ResourcesLibraryContext.Provider value={value}>
            {children}
        </ResourcesLibraryContext.Provider>
    );
}

export function ResourcesLibraryProvider({ children }: { children: ReactNode }) {
    return (
        <FaqProvider>
            <HelpDocumentProvider>
                <VideosProvider>
                    <ResourcesLibraryValue>{children}</ResourcesLibraryValue>
                </VideosProvider>
            </HelpDocumentProvider>
        </FaqProvider>
    );
}

export function useResourcesLibrary(): ResourcesLibraryContextValue {
    const context = useContext(ResourcesLibraryContext);
    if (context === undefined) {
        throw new Error('useResourcesLibrary must be used within a ResourcesLibraryProvider');
    }
    return context;
}
